import { c as _c } from "react/compiler-runtime";
import FaroSphereLogo from "@assets/logo/faro-sphere-xg-logo-color.svg?react";
import ErrorIcon from "@assets/illustrations/error_page.svg?react";
import { SphereSupportLink } from "@components/common/sphere-support-link";
import { UserMenu } from "@components/header/user-menu";
import { ContactSupportLocations } from "@custom-types/types";
import { Box } from "@mui-wrapper";
import { FontWeights, Text, Button } from "@flat-ui";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect } from "react";

/**
 * This is a custom error component which is shown if something goes wrong in routing.
 * e.g. typing a route that is not available.
 */
export function CustomErrorBoundary() {
  const $ = _c(12);
  const {
    trackAsyncEvent
  } = useTrackEvent();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = function handleRefresh() {
      window.location.reload();
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const handleRefresh = t0;
  let t1;
  if ($[1] !== trackAsyncEvent) {
    t1 = () => {
      try {
        trackAsyncEvent({
          name: "encountered unexpected error"
        });
      } catch {
        throw new Error("Failed to track async event: encountered unexpected error.");
      }
    };
    $[1] = trackAsyncEvent;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      backgroundColor: "surfaces.secondary",
      minHeight: "100vh"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Box component="div" sx={{
      pt: "14px",
      pr: "24px",
      pl: "8px",
      pb: "16px",
      display: "flex",
      justifyContent: "space-between"
    }}><FaroSphereLogo /><UserMenu /></Box>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      display: "flex",
      flexDirection: "column",
      alignItems: "center"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Box sx={{
      mt: "50px"
    }}><ErrorIcon width={400} height="auto" /></Box>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      mt: "37px",
      display: "flex",
      justifyContent: "center",
      flexDirection: "column",
      alignItems: "center",
      gap: "20px",
      maxWidth: "700px"
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Text variant="heading32" sx={{
      fontSize: "38px",
      color: "text.secondary",
      fontWeight: FontWeights.SemiBold
    }}>Something went wrong</Text>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      fontSize: "20px",
      color: "text.secondary",
      fontWeight: FontWeights.Regular
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Box sx={t6}>{t7}<Text variant="bodyL" sx={t8}>We encountered an unexpected issue, please refresh the page. If the issue persists, contact{" "}<SphereSupportLink location={ContactSupportLocations.criticalErrorPage} />.</Text></Box>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Box sx={t2}>{t3}<Box sx={t4}>{t5}{t9}<Button sx={{
          my: "85px"
        }} onClick={handleRefresh}>Refresh</Button></Box></Box>;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  return t10;
}